import type { ControllerFieldState } from 'react-hook-form';

export interface RhfErrorOptions {
  isSubmitted?: boolean;
}

/**
 * Maps RHF `fieldState` to inline error semantics:
 * show a message after the control has been touched OR the form has been submitted.
 *
 * Handles all RHF error shapes:
 * - Standard: { message: string }
 * - Array item errors (Zod validates each item): [{ message: string }] — take first item
 * - Array root error (Zod too_small/too_large on the array itself): { message: string }
 * - Nested error objects from zodResolver: unwraps .message safely
 */
export function rhfInlineError(
  fieldState: ControllerFieldState,
  options: RhfErrorOptions = {},
): string | undefined {
  if (fieldState.error == null) return undefined;
  if (!fieldState.isTouched && !options.isSubmitted) return undefined;
  const e = fieldState.error;

  // Simple string error
  if (typeof e === 'string') return e === '' ? undefined : e;

  // Array of item errors (e.g. Zod z.array(z.string()) failing per-item)
  // RHF stores these as: fieldState.error = [{ message: '...', type: '...' }, ...]
  if (Array.isArray(e)) {
    for (const item of e) {
      if (item && typeof item === 'object' && 'message' in item) {
        const m = (item as { message?: unknown }).message;
        if (typeof m === 'string' && m !== '') return m;
      }
    }
    // Array error but no readable message — provide a generic fallback
    return 'Invalid value';
  }

  // Standard RHF error object: { message?: string, type?: string, ... }
  if (typeof e === 'object' && e !== null && 'message' in e) {
    const m = (e as { message?: unknown }).message;
    if (typeof m === 'string' && m !== '') return m;
    // message exists but is not a readable string — skip to fallback
  }

  // root error (RHF v7 array field root): { root: { message: string } }
  if (
    typeof e === 'object' &&
    e !== null &&
    'root' in e &&
    typeof (e as { root?: unknown }).root === 'object'
  ) {
    const root = (e as { root: { message?: unknown } }).root;
    if (root && typeof root.message === 'string' && root.message !== '') return root.message;
  }

  // Final fallback — return undefined instead of "[object Object]" to avoid confusing users
  return undefined;
}
